import { useState, useEffect, useMemo, useCallback } from 'react';
import { Stack, Modal, IconButton, TextField, DefaultButton, ProgressIndicator } from '@fluentui/react';
import Loader from '../../components/Loader/Loader';
import { deleteCoverLetters, getCoverLettersApi, uploadCoverLetterDocuments } from '../../api/api';
import { ArrowUploadRegular } from '@fluentui/react-icons';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';
import { useIntl } from 'react-intl';

import styles from './CoverLetter.module.css';
import ReportList from '../../components/ReportList';
import ReportToolBar from '../../components/ReportToolBar';
import { useAuthErrorContext } from '../../components/AuthErrorProvider';
import { CoverLetterItem } from './types';
import { DeleteButton } from '../../components/DeleteButton/DeleteButton';
import { DeleteDialog } from '../../components/DeleteDialog/DeleteDialog';
import { extractCandidateName } from '../../utilities/nameExtractor';
import { ReportItem } from '../../components/ReportItem/ReportItem';
import { OrderByOption } from '../trafficlightreport/types';

const CoverLetter = () => {
  const { handleAuthError } = useAuthErrorContext();
  const [loadingAIDocuments, setLoadingAIDocuments] = useState(false);
  const [checkedItems, setCheckedItems] = useState<CoverLetterItem[]>([]);
  const [orderBy, setOrderBy] = useState({ property: 'createddate', desc: true });
  const [coverLetter, setCoverLetter] = useState<CoverLetterItem[]>([]);
  const [selectedCVFile, setSelectedCVFile] = useState<File | undefined>(undefined);
  const [selectedJDFile, setSelectedJDFile] = useState<File | undefined>(undefined);
  const [candidateName, setCandidateName] = useState<string | undefined>(undefined);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false); // State to handle Info modal
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false); // State to handle Upload modal
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const intl = useIntl();

  const isUploadModalInvalid = useMemo(
    () => !selectedCVFile || !selectedJDFile || !candidateName,
    [selectedCVFile, selectedJDFile, candidateName],
  );

  const validateFile = async (file: File): Promise<boolean> => {
    // Check if file is empty
    if (file.size === 0) {
      toast.error(intl.formatMessage({ id: 'coverLetter.upload.emptyFile' }));
      return false;
    }

    // Basic PDF validation - check for PDF header magic number
    if (file.type === 'application/pdf') {
      try {
        const arrayBuffer = await file.slice(0, 5).arrayBuffer();
        const header = new Uint8Array(arrayBuffer);
        const pdfHeader = '%PDF-'; // PDF magic number
        const headerString = new TextDecoder().decode(header);

        if (!headerString.startsWith(pdfHeader)) {
          toast.error(intl.formatMessage({ id: 'coverLetter.upload.corruptedPdf' }));
          return false;
        }
      } catch (error) {
        toast.error(intl.formatMessage({ id: 'coverLetter.upload.validationError' }));
        return false;
      }
    }

    // Basic DOCX validation - check for ZIP structure (DOCX files are ZIP-based)
    if (file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
      try {
        const arrayBuffer = await file.slice(0, 4).arrayBuffer();
        const header = new Uint8Array(arrayBuffer);
        const zipHeader = [0x50, 0x4b, 0x03, 0x04]; // ZIP magic number

        if (!zipHeader.every((byte, i) => header[i] === byte)) {
          toast.error(intl.formatMessage({ id: 'coverLetter.upload.corruptedDocx' }));
          return false;
        }
      } catch (error) {
        toast.error(intl.formatMessage({ id: 'coverLetter.upload.validationError' }));
        return false;
      }
    }

    return true;
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>, type: 'CV' | 'JD') => {
    if (event.target.files && event.target.files.length === 1) {
      const file = event.target.files[0];
      const validTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];

      if (!validTypes.includes(file.type)) {
        toast.error(intl.formatMessage({ id: 'coverLetter.upload.invalidFileType' }));
        return;
      }

      // Validate file integrity
      const isValid = await validateFile(file);
      if (!isValid) {
        event.target.value = ''; // Reset file input
        return;
      }

      if (type === 'CV') {
        setSelectedCVFile(file);
        const name = extractCandidateName(file.name);
        setCandidateName(name.name || file.name);
      } else {
        setSelectedJDFile(file);
      }
    } else {
      toast.warn(intl.formatMessage({ id: 'coverLetter.upload.selectFile' }));
    }
  };

  const handleUpload = async () => {
    if (!selectedCVFile || !selectedJDFile) {
      toast.error('Please select both CV and JD files');
      return;
    }

    if (!candidateName) {
      toast.error('Please enter candidate name');
      return;
    }

    try {
      setLoadingAIDocuments(true);
      const response = await uploadCoverLetterDocuments(candidateName, selectedCVFile, selectedJDFile, {});

      if (response.status !== 200) {
        toast.error('Failed to upload files');
        setLoadingAIDocuments(false);
        return;
      }

      setSelectedCVFile(undefined);
      setSelectedJDFile(undefined);
      setCandidateName(undefined);
      setIsUploadModalOpen(false);

      toast.success('Files uploaded successfully!');
      setIsLoading(true);
      fetchCoverLetters();
    } catch (error) {
      toast.error('Failed to upload files');
      setLoadingAIDocuments(false);
      return;
    } finally {
      setLoadingAIDocuments(false);
      setIsLoading(false);
    }
  };

  const handleDownload = async () => {
    const itemsToDownload = checkedItems.length > 0 ? checkedItems : coverLetter;

    if (itemsToDownload.length === 0) {
      toast.error('No files to download');
      return;
    }

    if (itemsToDownload.length === 1) {
      const item = itemsToDownload[0];
      if (item.fullpath) {
        const response = await fetch(item.fullpath);
        const blob = await response.blob();
        saveAs(blob, item.filename);
      }
    } else {
      const zip = new JSZip();
      await Promise.all(
        itemsToDownload.map(async item => {
          if (item.fullpath) {
            const response = await fetch(item.fullpath);
            const blob = await response.blob();
            zip.file(item.filename, blob);
          }
        }),
      );
      const content = await zip.generateAsync({ type: 'blob' });
      saveAs(content, `coverLetters_${new Date().toLocaleDateString()}_${new Date().toLocaleTimeString()}.zip`);
    }
  };

  const handleInfoModal = () => {
    setIsInfoModalOpen(true); // Open the Info modal
  };

  const fetchCoverLetters = useCallback(async () => {
    setIsLoading(true);
    const response = await getCoverLettersApi(undefined, handleAuthError);
    setCoverLetter((response.data || []) as CoverLetterItem[]);
    setIsLoading(false);
  }, [handleAuthError]);

  const orderByOptions: OrderByOption<CoverLetterItem>[] = [
    { key: 'createddate', text: 'Created Date', order: 'desc' },
    { key: 'filename', text: 'Name', order: 'asc' },
    { key: 'status', text: 'Status', order: 'asc' },
  ];

  useEffect(() => {
    setCheckedItems([]);
  }, [orderBy]);

  useEffect(() => {
    fetchCoverLetters();

    const intervalId = setInterval(fetchCoverLetters, 30000);

    return () => clearInterval(intervalId);
  }, [fetchCoverLetters]);

  const handleDelete = () => {
    if (coverLetter.length === 0) return;
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      const itemsToDelete = checkedItems.length
        ? checkedItems.map(item => ({ blob_name: item.filename }))
        : coverLetter.map(item => ({ blob_name: item.filename }));
      await deleteCoverLetters(itemsToDelete, handleAuthError);
      await fetchCoverLetters();
      setCheckedItems([]);
      toast.success(intl.formatMessage({ id: 'coverLetter.delete.success' }, { count: itemsToDelete.length }));
    } catch (error) {
      toast.error(intl.formatMessage({ id: 'coverLetter.delete.error' }));
    } finally {
      setIsDeleteDialogOpen(false);
    }
  };

  return (
    <div className={styles.container}>
      <Stack horizontal className={styles.chatRoot}>
        <Stack horizontal tokens={{ childrenGap: 20 }} style={{ width: '100%' }}>
          <Stack.Item grow={1} style={{ height: '100%' }}>
            <div
              style={{
                height: '100%',
                padding: '2rem',
                borderRadius: '1rem',
                background: 'white',
                position: 'relative',
              }}
            >
              {loadingAIDocuments ? (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                    width: '100%',
                  }}
                >
                  <Loader />
                </div>
              ) : (
                <>
                  {!coverLetter.length && (
                    <p
                      style={{
                        fontSize: '16px',
                        color: '#555',
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        textAlign: 'center',
                        zIndex: 1,
                      }}
                    >
                      {intl.formatMessage({ id: 'coverLetter.empty.message' })}
                    </p>
                  )}

                  <ReportToolBar orderByOptions={orderByOptions} setOrderBy={setOrderBy}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      {!!coverLetter.length && (
                        <>
                          <DeleteButton
                            itemCount={checkedItems.length}
                            onClick={handleDelete}
                            totalCount={coverLetter.length}
                          />
                          <DefaultButton
                            onClick={handleDownload}
                            disabled={!coverLetter.length}
                            iconProps={{ iconName: 'Download' }}
                            text={intl.formatMessage(
                              { id: 'coverLetter.buttons.download' },
                              { count: checkedItems.length },
                            )}
                          />
                        </>
                      )}
                      <DefaultButton
                        onClick={_ => setIsUploadModalOpen(true)}
                        iconProps={{ iconName: 'Upload' }}
                        text={intl.formatMessage({ id: 'coverLetter.buttons.upload' })}
                        primary
                      />
                      <DefaultButton
                        onClick={handleInfoModal}
                        iconProps={{ iconName: 'Info' }}
                        text={intl.formatMessage({ id: 'coverLetter.buttons.help' })}
                        primary
                      />
                    </div>
                  </ReportToolBar>
                  {isLoading ? <ProgressIndicator /> : <ProgressIndicator percentComplete={0} />}
                  {!!coverLetter.length && (
                    <ReportList
                      orderBy={orderBy}
                      items={coverLetter}
                      onChecked={checkedItems => {
                        setCheckedItems(checkedItems);
                      }}
                      renderContent={item => (
                        <ReportItem
                          filename={item.filename}
                          status={item.status}
                          details={{
                            candidate_name: item.details?.candidate_name,
                            processed: item.details?.processed ?? item.details?.updated,
                          }}
                          progress={item.progress}
                        />
                      )}
                    />
                  )}
                </>
              )}
            </div>
          </Stack.Item>
        </Stack>
      </Stack>
      <Modal isOpen={isInfoModalOpen} onDismiss={() => setIsInfoModalOpen(false)} isBlocking={true}>
        <div style={{ padding: '20px', position: 'relative' }}>
          <IconButton
            iconProps={{ iconName: 'Cancel' }}
            onClick={() => setIsInfoModalOpen(false)}
            style={{
              position: 'absolute',
              top: '10px',
              right: '10px',
            }}
          />
          <h2>{intl.formatMessage({ id: 'coverLetter.info.title' })}</h2>
          <p>{intl.formatMessage({ id: 'coverLetter.info.description' })}</p>
        </div>
      </Modal>
      <Modal isOpen={isUploadModalOpen} onDismiss={() => setIsUploadModalOpen(false)} isBlocking={true}>
        <div style={{ padding: '20px', position: 'relative' }}>
          <IconButton
            iconProps={{ iconName: 'Cancel' }}
            onClick={() => setIsUploadModalOpen(false)}
            style={{
              position: 'absolute',
              top: '10px',
              right: '10px',
            }}
          />
          <h2>{intl.formatMessage({ id: 'coverLetter.upload.title' })}</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <input
              type="file"
              onChange={e => handleFileChange(e, 'CV')}
              style={{
                display: 'none',
              }}
              id="file-upload-cv"
              accept=".docx,.pdf"
            />
            <label
              htmlFor="file-upload-cv"
              style={{
                cursor: 'pointer',
                padding: '0.5rem 1rem',
                fontSize: '14px',
                fontWeight: '600',
                borderRadius: '4px',
                color: '#242424',
                border: '1px solid #D1D1D1',
                background: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.25rem',
                height: '32px',
              }}
            >
              <ArrowUploadRegular style={{ marginTop: 'auto', marginBottom: 'auto' }} /> Upload CV
            </label>
            {selectedCVFile && <span>{selectedCVFile.name}</span>}
            <input
              type="file"
              onChange={e => handleFileChange(e, 'JD')}
              style={{
                display: 'none',
              }}
              id="file-upload-jd"
              accept=".docx,.pdf"
            />
            <label
              htmlFor="file-upload-jd"
              style={{
                cursor: 'pointer',
                padding: '0.5rem 1rem',
                fontSize: '14px',
                fontWeight: '600',
                borderRadius: '4px',
                color: '#242424',
                border: '1px solid #D1D1D1',
                background: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.25rem',
                height: '32px',
              }}
            >
              <ArrowUploadRegular style={{ marginTop: 'auto', marginBottom: 'auto' }} /> Upload JD
            </label>
            {selectedJDFile && <span>{selectedJDFile.name}</span>}
            <TextField
              label="Candidate Name"
              value={candidateName}
              onChange={(_, newValue) => setCandidateName(newValue)}
              required
            />
            <DefaultButton
              onClick={handleUpload}
              disabled={isUploadModalInvalid}
              text="Submit"
              primary={!isUploadModalInvalid}
            />
          </div>
        </div>
      </Modal>
      <DeleteDialog
        hidden={!isDeleteDialogOpen}
        onDismiss={() => setIsDeleteDialogOpen(false)}
        onConfirm={confirmDelete}
        itemCount={checkedItems.length || coverLetter.length}
        messageId="coverLetter.delete.confirmMessage"
        titleId="coverLetter.delete.confirmTitle"
      />
    </div>
  );
};

export default CoverLetter;
